import { forwardRef, type ReactNode } from 'react'
import { isString } from 'lodash-es'
import type { TypographyProps, StackProps } from '@mui/material'
import { Trans } from 'react-i18next'
import { isComponentOfKind } from 'lib/helpers/isComponentOfKind/isComponentOfKind'
import * as $ from './Card.styled'

type CardSlotProps = Omit<StackProps, 'children'>

export interface CardProps
  extends Partial<Pick<$.RootProps, 'id' | 'size' | 'corners' | 'margin' | 'sx' | 'fullWidth' | 'children'>> {
  'data-testid'?: string
  minHeight?: number
  slots?: {
    left?: ReactNode
    content: ReactNode
    right?: ReactNode
  }
  slotProps?: {
    left?: CardSlotProps
    content?: TypographyProps
    right?: CardSlotProps
    flexWrap?: CardSlotProps
  }
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      slots,
      slotProps,
      children,
      'data-testid': dataTestId,
      minHeight = 40,
      corners = 'rounded',
      size = 'medium',
      margin = 'normal',
      fullWidth = false,
      ...props
    },
    ref,
  ) => {
    const isContentTrans = isComponentOfKind(slots?.content, Trans)

    return (
      <$.Root
        corners={corners}
        ref={ref}
        data-testid={dataTestId}
        size={size}
        margin={margin}
        fullWidth={fullWidth}
        {...props}
      >
        {slots && (
          <$.SlotsWrapper size={size}>
            {slots.left && (
              <$.LeftSlot size={size} minHeight={minHeight} {...slotProps?.left}>
                {slots.left}
              </$.LeftSlot>
            )}
            <$.FlexWrap size={size} {...slotProps?.flexWrap}>
              <$.ContentSlot
                variant="body2"
                minHeight={minHeight}
                component={isString(slots?.content) || isContentTrans ? 'p' : 'div'}
                {...slotProps?.content}
              >
                {isContentTrans ? <span>{slots.content}</span> : slots.content}
              </$.ContentSlot>
              {slots.right && (
                <$.RightSlot minHeight={minHeight} {...slotProps?.right}>
                  {slots.right}
                </$.RightSlot>
              )}
            </$.FlexWrap>
          </$.SlotsWrapper>
        )}
        {children}
      </$.Root>
    )
  },
)

Card.displayName = 'Card'
